"use client";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CribNoshLogo } from "@/components/ui/CribNoshLogo";
import { api } from "@/convex/_generated/api";
import { handleAuthSuccess } from "@/lib/auth-client";
import { useAction } from "convex/react";
import Image from "next/image";

import { Mail, X, ArrowLeft } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { EmailOTPVerification } from "./email-otp-verification";

interface EmailSignInModalProps {
  isVisible: boolean;
  onClose: () => void;
  onEmailSubmit?: (email: string) => void;
  onSignInSuccess?: () => void;
  sourceApp?: "web" | "food_creator" | "driver" | "mobile";
  title?: string;
  subtitle?: string;
  emailPlaceholder?: string;
  continueLabel?: string;
}

export function EmailSignInModal({
  isVisible,
  onClose,
  onEmailSubmit,
  onSignInSuccess,
  sourceApp = "web",
  title = "Get started with CribNosh",
  subtitle = "Enter your email to receive a verification code and find your perfect meal.",
  emailPlaceholder = "Email address",
  continueLabel = "Continue",
}: EmailSignInModalProps) {
  const [email, setEmail] = useState("");
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [showOTPVerification, setShowOTPVerification] = useState(false);
  const [error, setError] = useState("");
  const [testOtp, setTestOtp] = useState<string | null>(null);

  useEffect(() => {
    if (isVisible) {
      document.body.classList.add('hide-site-header');
    } else {
      document.body.classList.remove('hide-site-header');
    }
    return () => document.body.classList.remove('hide-site-header');
  }, [isVisible]);

  const sendEmailOTP = useAction(api.actions.users.customerEmailSendOTP);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSendingOTP) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSendingOTP(true);
    setError("");

    try {
      onEmailSubmit?.(email);

      // Send OTP via direct Convex action
      const data = await sendEmailOTP({ email, sourceApp });

      if (data.success) {
        if (data.testOtp) {
          setTestOtp(data.testOtp);
          toast.info('Development Mode', {
            description: `OTP Code: ${data.testOtp}`,
            duration: 10000,
          });
        }
        setShowOTPVerification(true);
      } else {
        const errorMessage = data.error || 'Failed to send verification code. Please try again.';
        setError(errorMessage);
        toast.error('Failed to Send Code', {
          description: errorMessage,
        });
      }
    } catch (err) {
      console.error('OTP send error:', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleOTPSuccess = async (token: string, _user: unknown) => {
    if (!token) {
      setError('Authentication failed. Please try again.');
      return;
    }

    await handleAuthSuccess(token);
    onClose();
    setEmail("");
    setShowOTPVerification(false);
    onSignInSuccess?.();
  };

  const handleOTPError = (error: string) => {
    setError(error);
  };

  const handleBack = () => {
    setShowOTPVerification(false);
    setError("");
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value.toLowerCase().trim());
    setError("");
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, y: "100%" }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: "100%" }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed inset-0 bg-[#02120A] z-[9999] overflow-hidden"
        >
          {/* Background Decorative Element (Packaging) - Desktop Only */}
          <div className="hidden lg:block absolute right-[-5%] bottom-[-10%] w-[600px] h-[600px] opacity-40 select-none pointer-events-none">
            <Image
              src="/images/cribnoshpackaging.png"
              alt=""
              width={600}
              height={600}
              className="object-contain transform rotate-[-15deg]"
            />
          </div>

          {/* Top Branding & Close Button Area */}
          <div className="absolute top-0 left-0 right-0 h-32 px-8 md:px-12 flex items-center justify-between z-20">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="flex items-center"
            >
              <CribNoshLogo variant="white" size={140} />
            </motion.div>

            <button
              onClick={onClose}
              className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-all active:scale-95"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Main Content Area */}
          <div className="h-full flex flex-col justify-center px-8 md:px-12 max-w-4xl relative z-10">
            <div className="w-full max-w-lg">
              {!showOTPVerification ? (
                <motion.div
                  initial={{ opacity: 0, x: -25 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                >
                  <h2 className="text-[36px] md:text-[48px] leading-[1.1] font-bold text-white mb-6 font-asgard tracking-[-1px]">
                    {title}
                  </h2>
                  <p className="text-[18px] md:text-[20px] leading-7 text-[#E5E7EB] mb-12 opacity-80 font-satoshi max-w-[380px]">
                    {subtitle}
                  </p>

                  <form onSubmit={handleEmailSubmit} className="space-y-8">
                    <div className="relative group">
                      <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-[#E6FFE8] opacity-50 group-focus-within:opacity-100 transition-opacity pointer-events-none" />
                      <Input
                        type="email"
                        placeholder={emailPlaceholder}
                        value={email}
                        onChange={handleEmailChange}
                        className="w-full h-16 bg-white/5 border-white/10 text-white text-xl placeholder:text-white/30 pl-14 pr-6 focus:border-white/40 focus:bg-white/10 transition-all rounded-2xl"
                        required
                        autoFocus
                      />
                    </div>

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-red-400 text-base font-medium flex items-center gap-2"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                        {error}
                      </motion.div>
                    )}

                    <Button
                      type="submit"
                      disabled={!email || isSendingOTP}
                      className="w-full h-16 bg-[#ff3b30] hover:bg-[#ff5e54] text-white font-bold text-xl rounded-2xl shadow-[0_8px_30px_rgb(255,59,48,0.2)] transition-all active:scale-[0.98] disabled:opacity-50 disabled:grayscale"
                    >
                      {isSendingOTP ? (
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 border-3 border-[#02120A] border-t-transparent rounded-full animate-spin" />
                          <span>Sending code...</span>
                        </div>
                      ) : (
                        continueLabel
                      )}
                    </Button>
                  </form>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, x: 25 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-8"
                >
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-3 text-white/60 hover:text-white mb-4 transition-all hover:translate-x-[-4px]"
                  >
                    <ArrowLeft className="w-6 h-6" />
                    <span className="text-lg font-medium">Back to email</span>
                  </button>

                  <EmailOTPVerification
                    email={email}
                    onBack={handleBack}
                    onSuccess={handleOTPSuccess}
                    onError={handleOTPError}
                    testOtp={testOtp}
                    sourceApp={sourceApp}
                  />
                </motion.div>
              )}
            </div>
          </div>

          {/* Bottom Depth / Texture */}
          <div className="fixed bottom-0 left-0 right-0 h-[30vh] bg-gradient-to-t from-black/60 to-transparent pointer-events-none" />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
